import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar, User, Clock, ChevronDown, ChevronRight, Search, ArrowLeft, Filter, RotateCcw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ACTION_COLORS = {
  Assigned: "bg-[#2F5D8A]/10 text-[#2F5D8A] border-[#2F5D8A]/25",
  Completed: "bg-[#6B8F62]/10 text-[#6B8F62] border-[#6B8F62]/25",
  Hold: "bg-[#D1A054]/10 text-[#7A6535] border-[#D1A054]/30",
  Released: "bg-[#8B6DB0]/10 text-[#6B5B8D] border-[#8B6DB0]/25",
  Returned: "bg-[#C07070]/10 text-[#C07070] border-[#C07070]/25",
  Transmitted: "bg-[#3B7A9C]/10 text-[#3B7A9C] border-[#3B7A9C]/25",
  Billing: "bg-[#6B8F62]/15 text-[#4A7A56] border-[#6B8F62]/30",
  Unassigned: "bg-gray-100 text-gray-500 border-gray-300",
  Reopened: "bg-amber-50 text-amber-700 border-amber-200",
  Updated: "bg-gray-50 text-gray-600 border-gray-200",
  Protected: "bg-red-50 text-red-600 border-red-200",
};

const ActionBadge = ({ action }) => {
  const cls = ACTION_COLORS[action] || "bg-gray-100 text-gray-500 border-gray-300";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}>
      {action}
    </span>
  );
};

const ClosingHistoryPage = ({ Layout }) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [selectedInspector, setSelectedInspector] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [summary, setSummary] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedInspector, setExpandedInspector] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderTimeline, setOrderTimeline] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const fetchDates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/closing-logs/dates`);
      setAvailableDates(res.data || []);
    } catch {}
  }, []);

  const fetchInspectors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/closing-logs/inspectors`, { params: { date: selectedDate } });
      setInspectors(res.data || []);
    } catch {}
  }, [selectedDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (selectedInspector) params.inspector = selectedInspector;
      const [sumRes, logRes] = await Promise.all([
        axios.get(`${API}/closing-logs/summary`, { params: { date: selectedDate } }),
        axios.get(`${API}/closing-logs`, { params }),
      ]);
      setSummary(sumRes.data || []);
      setLogs(logRes.data || []);
    } catch {}
    setLoading(false);
  }, [selectedDate, selectedInspector]);

  useEffect(() => { fetchDates(); }, [fetchDates]);
  useEffect(() => { fetchInspectors(); }, [selectedDate, fetchInspectors]);
  useEffect(() => { fetchData(); }, [selectedDate, selectedInspector, fetchData]);

  const fetchOrderTimeline = async (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
      setOrderTimeline([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/closing-logs/order-timeline/${orderId}`);
      setOrderTimeline(res.data || []);
      setExpandedOrder(orderId);
    } catch {
      setOrderTimeline([]);
    }
  };

  const handleBackfillYesterday = async () => {
    if (!window.confirm("This will reconstruct yesterday's closing logs from current order data. Continue?")) return;
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`${API}/closing-logs/backfill-yesterday`);
      setBackfillResult(res.data);
      // Refresh data to show new entries
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;
      setSelectedDate(yStr);
      fetchDates();
    } catch (err) {
      setBackfillResult({ error: err.response?.data?.detail || "Backfill failed" });
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleDeleteBackfill = async (date) => {
    if (!window.confirm(`Delete all backfill entries for ${date}?`)) return;
    try {
      const res = await axios.delete(`${API}/closing-logs/backfill/${date}`);
      setBackfillResult({ deleted: res.data.deleted, date });
      fetchData();
      fetchDates();
    } catch (err) {
      setBackfillResult({ error: err.response?.data?.detail || "Delete failed" });
    }
  };

  const filteredSummary = selectedInspector
    ? summary.filter(s => s.inspector === selectedInspector)
    : summary;

  const filteredLogs = searchTerm
    ? logs.filter(l => l.order_id?.includes(searchTerm) || l.inspector_name?.toLowerCase().includes(searchTerm.toLowerCase()))
    : logs;

  const totalActions = logs.length;
  const uniqueOrders = new Set(logs.map(l => l.order_id)).size;
  const uniqueInspectors = new Set(logs.map(l => l.inspector_name).filter(Boolean)).size;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr + "T00:00:00");
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return `${days[d.getDay()]}, ${dateStr}`;
    } catch { return dateStr; }
  };

  return (
    <Layout>
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="closing-history-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#B08A3E]" />
              Daily Closing History
            </h1>
            <p className="text-gray-500 text-sm">Passive log of all dispatch and inspector actions</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBackfillYesterday}
              disabled={backfillLoading}
              className="px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40 transition-colors"
              data-testid="backfill-yesterday-btn"
            >
              <RotateCcw className="h-4 w-4" />
              {backfillLoading ? "Processing..." : "Backfill Yesterday"}
            </button>
          </div>
        </div>

        {/* Backfill Result */}
        {backfillResult && (
          <div className={`rounded-lg border p-3 text-sm ${backfillResult.error ? 'bg-red-50 border-red-200 text-red-700' : backfillResult.deleted !== undefined ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-[#A7C4A0]/15 border-[#A7C4A0]/40 text-[#4A7A56]'}`} data-testid="backfill-result">
            {backfillResult.error ? (
              <p>{backfillResult.error}</p>
            ) : backfillResult.deleted !== undefined ? (
              <p>Deleted {backfillResult.deleted} backfill entries for {backfillResult.date}</p>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Backfill complete for {backfillResult.backfill_date}</p>
                  <p className="text-xs mt-0.5">{backfillResult.created} entries: {backfillResult.completed} completed, {backfillResult.transmitted} transmitted, {backfillResult.hold} hold, {backfillResult.returned} returned</p>
                </div>
                <button
                  onClick={() => handleDeleteBackfill(backfillResult.backfill_date)}
                  className="px-2 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200 flex items-center gap-1"
                  data-testid="delete-backfill-btn"
                >
                  <Trash2 className="h-3 w-3" />
                  Undo
                </button>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-gray-200 p-3" data-testid="closing-history-filters">
          <Filter className="h-4 w-4 text-gray-400" />
          <div>
            <label className="block text-[9px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:border-[#D1A054]"
              data-testid="closing-date-filter"
            />
          </div>
          <div>
            <label className="block text-[9px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">Inspector</label>
            <select
              value={selectedInspector}
              onChange={e => setSelectedInspector(e.target.value)}
              className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:border-[#D1A054]"
              data-testid="closing-inspector-filter"
            >
              <option value="">All Inspectors</option>
              {inspectors.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[9px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">Search Order</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Order ID..."
                className="pl-7 pr-2 py-1 bg-gray-50 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:border-[#D1A054] w-40"
                data-testid="closing-search"
              />
            </div>
          </div>
        </div>

        {/* Day Summary Bar */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-gray-500 font-medium">{formatDate(selectedDate)}</span>
          <span className="text-[#1F2937] font-bold">{totalActions} actions</span>
          <span className="text-gray-400">{uniqueOrders} orders</span>
          <span className="text-gray-400">{uniqueInspectors} inspectors</span>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-gray-400" data-testid="closing-empty">
            <Clock className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No closing logs for this date</p>
            <p className="text-xs mt-1">Actions will appear here automatically as dispatchers and inspectors work</p>
          </div>
        ) : (
          <>
            {/* Inspector Summary Cards */}
            <div className="space-y-3" data-testid="closing-summary">
              {filteredSummary.map(group => {
                const isExpanded = expandedInspector === group.inspector;
                return (
                  <div key={group.inspector} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`closing-inspector-${group.inspector}`}>
                    {/* Inspector Header */}
                    <button
                      onClick={() => setExpandedInspector(isExpanded ? null : group.inspector)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/50 transition-colors"
                      data-testid={`closing-inspector-toggle-${group.inspector}`}
                    >
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-[#B08A3E]" />
                        <span className="font-semibold text-[#1F2937] text-sm">{group.inspector}</span>
                        <span className="text-gray-400 text-xs">{group.actions.length} actions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {Object.entries(group.counts).map(([action, count]) => (
                          <span key={action} className="flex items-center gap-1">
                            <ActionBadge action={action} />
                            <span className="text-[10px] text-gray-500 font-medium">{count}</span>
                          </span>
                        ))}
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                      </div>
                    </button>

                    {/* Expanded Actions */}
                    {isExpanded && (
                      <div className="border-t border-gray-100">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50/80">
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Time</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Order</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Action</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Status</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">User</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Notes</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2 w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.actions.map((log, idx) => (
                              <React.Fragment key={idx}>
                                <tr
                                  className={`border-t border-gray-50 hover:bg-[#D1A054]/5 cursor-pointer transition-colors ${expandedOrder === log.order_id ? 'bg-[#D1A054]/5' : ''}`}
                                  onClick={() => fetchOrderTimeline(log.order_id)}
                                  data-testid={`closing-log-row-${idx}`}
                                >
                                  <td className="px-4 py-2 text-gray-500 text-xs font-mono">{log.action_time || "-"}</td>
                                  <td className="px-4 py-2 font-medium text-[#1F2937]">{log.order_id}</td>
                                  <td className="px-4 py-2"><ActionBadge action={log.action_type} /></td>
                                  <td className="px-4 py-2 text-xs text-gray-500">
                                    {log.prev_status && log.new_status ? `${log.prev_status} → ${log.new_status}` : log.new_status || "-"}
                                  </td>
                                  <td className="px-4 py-2 text-xs text-gray-600">{log.user}</td>
                                  <td className="px-4 py-2 text-xs text-gray-400 max-w-[200px] truncate" title={log.notes}>{log.notes || "-"}</td>
                                  <td className="px-4 py-2">
                                    {expandedOrder === log.order_id
                                      ? <ChevronDown className="h-3.5 w-3.5 text-[#B08A3E]" />
                                      : <ChevronRight className="h-3.5 w-3.5 text-gray-300" />}
                                  </td>
                                </tr>
                                {/* Order Timeline Expansion */}
                                {expandedOrder === log.order_id && (
                                  <tr>
                                    <td colSpan={7} className="px-4 py-3 bg-[#F9F6F0]">
                                      <div className="pl-6 border-l-2 border-[#D1A054]/40 space-y-2">
                                        <p className="text-[10px] font-bold text-[#B08A3E] uppercase tracking-wider mb-1">Full Timeline — Order {log.order_id}</p>
                                        {orderTimeline.length === 0 ? (
                                          <p className="text-xs text-gray-400">Loading timeline...</p>
                                        ) : (
                                          orderTimeline.map((t, ti) => (
                                            <div key={ti} className="flex items-center gap-3 text-xs">
                                              <span className="text-gray-400 font-mono w-20 flex-shrink-0">{t.action_date} {t.action_time}</span>
                                              <ActionBadge action={t.action_type} />
                                              <span className="text-gray-600">{t.user}</span>
                                              {t.notes && <span className="text-gray-400 truncate max-w-[300px]" title={t.notes}>{t.notes}</span>}
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Full Log Table (when searching or when an inspector is selected) */}
            {searchTerm && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="closing-search-results">
                <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-500">Search results: {filteredLogs.length} entries</p>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Time</th>
                      <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Order</th>
                      <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Inspector</th>
                      <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Action</th>
                      <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">User</th>
                      <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, idx) => (
                      <tr key={idx} className="border-t border-gray-50 hover:bg-gray-50/50">
                        <td className="px-4 py-2 text-gray-500 text-xs font-mono">{log.action_time || "-"}</td>
                        <td className="px-4 py-2 font-medium text-[#1F2937]">{log.order_id}</td>
                        <td className="px-4 py-2 text-xs text-[#B08A3E]">{log.inspector_name || "-"}</td>
                        <td className="px-4 py-2"><ActionBadge action={log.action_type} /></td>
                        <td className="px-4 py-2 text-xs text-gray-600">{log.user}</td>
                        <td className="px-4 py-2 text-xs text-gray-400 max-w-[200px] truncate" title={log.notes}>{log.notes || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ClosingHistoryPage;
